import React, { useMemo } from 'react';
import { CalendarEvent } from 'interfaces/calendar.interface.js';
import { ErrorCode, ErrorIcons } from 'components/other/_ErrorIcons';
import Status from 'interfaces/status.class';
import { dayjs } from '@umanforge/common';

interface ChronogramEventWeekProps {
  event: CalendarEvent
  lineId: string;
  openModal: ( event: CalendarEvent ) => void
  onDragEnd: ( ) => void;
  onDragStart: ( event: CalendarEvent ) => void;
  onOver?: ( id: string | undefined ) => void;
  draggable?: boolean;
  className?: string;
  weekStart: dayjs.Dayjs;
  stackable?: boolean;
  isAdmin?: boolean;
}

export const ChronogramEventWeek: React.FC<ChronogramEventWeekProps> = ( {
  event,
  lineId,
  openModal,
  onDragEnd,
  onDragStart,
  draggable = true,
  className = '',
  onOver = () => { /* */},
  weekStart,
  stackable = true,
  isAdmin = false
} ) => {

  const { colStart, colEnd, errors } = useMemo( () => {
    let eventStart = event.start?.getDay() ?? 0;
    let eventEnd = event.end?.getDay() ?? 0;

    if ( dayjs( event.start ).isBefore( weekStart.startOf( 'day' ) ) ) {
      eventStart = 1;
    } else {
      eventStart = ( event.start?.getDay() ?? 0 ) == 0 ? 7 : ( event.start?.getDay() ?? 0 );
    }

    if ( dayjs( event.end ).isAfter( weekStart.add( 6, 'day' ).endOf( 'day' ) ) ) {
      eventEnd = 8;
    } else {
      eventEnd = ( event.end?.getDay() ?? 0 ) == 0 ? 8 : ( event.end?.getDay() ?? 0 ) + 1;
    }

    const _errors: ErrorCode[] = [];
    if ( event.understaffed ) { _errors.push( 'understaffed' ); }
    if ( event.task?.location == null ) { _errors.push( 'no_location' ); }
    if ( event.title == null ) { _errors.push( 'error' ); }
    if ( stackable && event.stacked && lineId !== 'not_assigned' ) { _errors.push( 'stacked' ); }
    return {
      colStart: eventStart,
      colEnd: eventEnd,
      errors: _errors
    };
  }, [ event ] );

  const { isPastEvent } = useMemo( () => {
    return {
      isPastEvent: dayjs( event.start ) < dayjs() //&& false
    };
  }, [ event ] );

  return <div
    className={'max-h-6 px-0.5 my-px z-10 relative group ' + className}
    style={{
      gridColumnStart: colStart,
      gridColumnEnd: colEnd
    }}
  >
    <div className={'h-full'} onClick={() => { openModal( event ); }}>
      <div
        className={`w-full h-full -z-10 ${ ( ( !isPastEvent && draggable ) || !isAdmin ) ? 'dragable-element cursor-pointer' : 'cursor-not-allowed'} `}
        draggable={ !isPastEvent && draggable }

        onDragStart={ e => {
          e.dataTransfer.setDragImage( new Image(), 0, 0 );
          if ( isPastEvent ) {return;}
          onDragStart( event );
        }}

        onDragEnd={ () => {
          if ( isPastEvent ) {return;}
          onDragEnd( );
        }}

        onMouseOver={ () => {
          onOver( event.id );
        } }
        onMouseLeave={ () => {
          onOver( undefined );
        } }
      >
        <div
          id="event_task"
          className={'relative w-full h-6 inline-flex rounded-sm prevent-select ' + ( ( event.understaffed ) && ' understaffed ' ) + ( ( stackable && event.stacked && lineId != 'not_assigned' ) && ' stacked-task' )}
          style={{ backgroundColor: Status.statusToColor( event.task?.status as string ) }}
        >
          <div className='w-fit inline-flex place-items-center'>
            { errors.map( ( err, i ) => {
              return <ErrorIcons error={err} key={`${i}_${err}`} size={5}/>;
            } ) }
          </div>
          <div className='overflow-hidden'>
            <span className='text-xs sm:whitespace-nowrap truncate flex items-center h-full gap-x-1'>
              { event.title }
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>;
};